import { HttpClient, HttpHeaders } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { AuthenticationService } from '@app/auth-management/authentication.service'
import { environment } from '@env/environment'
import { removeUndefinedParams } from '@app/common/remove-undefined-params'
import { Observable } from 'rxjs'

@Injectable({
  providedIn: 'root',
})
export class PlatformAccessService {
  private readonly baseUrl = environment.PLATFORM_ACCESS_BASE_URL
  private readonly http: HttpClient = inject(HttpClient)
  private readonly authentication: AuthenticationService = inject(AuthenticationService)

  request(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    params?: Record<string, string | number | boolean>,
    body?: object
  ): Observable<object> {
    // platform-access selects its token-validation strategy from `x-auth-version`;
    // send the stored version (v3 for SSO sessions) so a Cognito-backed token isn't
    // validated as a legacy one. Omitted when unset (legacy `?token=` handoff).
    const headerConfig: Record<string, string> = {
      Authorization: this.authentication.identityToken$.value as string,
    }
    const authVersion = this.authentication.authVersion
    if (authVersion) {
      headerConfig['x-auth-version'] = authVersion
    }
    const headers = new HttpHeaders(headerConfig)

    const url = `${this.baseUrl}${path}`

    switch (method) {
      case 'GET': {
        return this.http.get(url, { params, headers })
      }
      case 'POST': {
        return this.http.post(url, body, { headers })
      }
      case 'PUT': {
        return this.http.put(url, body, { headers })
      }
      case 'PATCH': {
        return this.http.patch(url, body, { headers })
      }
      case 'DELETE': {
        return this.http.delete(url, { headers })
      }
      default: {
        throw new Error(`Can not send request, invalid HttpMethod ${method}`)
      }
    }
  }

  get(path: string, params?: object) {
    const validatedParams = removeUndefinedParams(params || {}) as Record<string, string | number | boolean>
    return this.request('GET', path, validatedParams)
  }

  post<T>(path: string, body?: object) {
    return this.request('POST', path, undefined, body) as Observable<T>
  }

  put<T>(path: string, body?: object) {
    return this.request('PUT', path, undefined, body) as Observable<T>
  }

  patch<T>(path: string, body?: object) {
    return this.request('PATCH', path, undefined, body) as Observable<T>
  }

  delete<T>(path: string) {
    return this.request('DELETE', path) as Observable<T>
  }
}
